import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { ProgressBar } from './components/ProgressBar'
import { HandoffModal } from './components/HandoffModal'
import { Smartphone } from 'lucide-react'
import { LanguageSelector } from './components/LanguageSelector'
import { MaintenancePage } from './components/MaintenancePage'
import { AlreadyCompletedPage } from './pages/AlreadyCompletedPage'
import { LinkRequiredPage } from './pages/LinkRequiredPage'
import { isPatientSessionUsable, PATIENT_TOKEN_REQUIRED_EVENT } from './utils/patientSession'
import { Logo } from './components/Logo'
import { TestModeProvider } from './context/TestModeContext'
import { useTenant, ContractConfig, resolvePaymentStep } from './context/TenantContext'
import { settingsService, patientLinkService, getApiErrorCode } from './services/api'
import { logFlowEvent, disableFlowEvents } from './services/flowEventService'
import { useOnboardingStore } from './store/onboardingStore'
import { enableLogging } from './utils/logger'
import { Step1Welcome } from './pages/Step1Welcome'
import { Step2PersonalData } from './pages/Step2PersonalData'
import { Step3AddressData } from './pages/Step3AddressData'
import { Step4Insurance } from './pages/Step4Insurance'
import { Step4DocumentUpload } from './pages/Step4DocumentUpload'
import { Step6DataSummary } from './pages/Step6DataSummary'
import { ContractSigningStep } from './pages/ContractSigningStep'
import { Step9PhoneVerification } from './pages/Step9PhoneVerification'
import { Step11SepaDebit } from './pages/Step11SepaDebit'
import { CardPaymentStep } from './pages/CardPaymentStep'
import { Step12Complete } from './pages/Step12Complete'
import { LegalFooter } from './components/LegalFooter'
import { flowFor, stepLabel, type FlowStep } from './flow/flow'
import { resolvePtContracts, ptContractKeysFor } from './flow/ptContracts'
import { PtDetailsStep } from './pages/psychotherapy/PtDetailsStep'
import { PtPaymentStep } from './pages/psychotherapy/PtPaymentStep'

/*
 * The step order lives in src/flow/flow.ts (one list per cohort). App only
 * renders steps[currentStep - 1]; currentStep stays a persisted 1-based index.
 */

// Default contracts (fallback when org has none configured)
const DEFAULT_CONTRACTS: ContractConfig[] = [
  {
    key: 'treatment_contract', documentIndex: 1, consentType: 'treatment_contract',
    downloadFilename: 'Behandlungsvertrag.pdf',
    title: { de: 'Behandlungsvertrag', en: 'Treatment Contract', it: 'Contratto di trattamento', es: 'Contrato de tratamiento' },
    description: { de: 'Behandlungsvertrag Beschreibung', en: 'Treatment contract description' },
    downloadLabel: { de: 'Behandlungsvertrag herunterladen', en: 'Download Treatment Contract' },
  },
  {
    key: 'privacy_policy', documentIndex: 2, consentType: 'privacy_policy',
    downloadFilename: 'Datenschutzerklaerung.pdf',
    title: { de: 'Datenschutzerklärung', en: 'Privacy Policy', it: 'Informativa sulla privacy', es: 'Política de privacidad' },
    description: { de: 'Datenschutz Beschreibung', en: 'Privacy policy description' },
    downloadLabel: { de: 'Datenschutzerklärung herunterladen', en: 'Download Privacy Policy' },
  },
  {
    key: 'billing_consent', documentIndex: 0, consentType: 'billing_consent',
    downloadFilename: 'Patienteneinwilligung.pdf',
    title: { de: 'Patienteneinwilligung', en: 'Patient Consent', it: 'Consenso del paziente', es: 'Consentimiento del paciente' },
    description: { de: 'Patienteneinwilligung Beschreibung', en: 'Patient consent description' },
    downloadLabel: { de: 'Patienteneinwilligung herunterladen', en: 'Download Patient Consent' },
  },
]

function App() {
  const { t } = useTranslation()
  const { tenant } = useTenant()

  // Get contracts from tenant config or use defaults
  // null = not configured → use defaults; [] = explicitly no contracts → skip signing
  const contracts: ContractConfig[] = tenant?.contracts === undefined || tenant?.contracts === null
    ? DEFAULT_CONTRACTS
    : (tenant.contracts as ContractConfig[])
  const hasContracts = contracts.length > 0

  // SEPA can be skipped by tenant config OR via ?nosepa=1 URL param
  // (used to send links to existing patients who already have SEPA active)
  const [skipSepaOverride] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    const param = new URLSearchParams(window.location.search).get('nosepa')
    if (param === '0' || param === 'false') {
      // Explicit unstick — the flag otherwise persists for the whole tab session
      sessionStorage.removeItem('skip-sepa')
      return false
    }
    if (sessionStorage.getItem('skip-sepa') === 'true') return true
    if (param === '1' || param === 'true') {
      sessionStorage.setItem('skip-sepa', 'true')
      return true
    }
    return false
  })
  const [testMode, setTestMode] = useState(false)

  // One org setting decides the payment step: none | sepa_test | sepa |
  // card_test | card. SEPA and card are mutually exclusive by construction,
  // and the *_test variants show the step to testers only.
  // The cohort was pinned when the magic link was exchanged (server services/cohort.ts)
  const cohort = useOnboardingStore((s) => s.cohort)
  const ptContractKeys = useOnboardingStore((s) => s.ptContractKeys)
  const ptContractKeysByInsurance = useOnboardingStore((s) => s.ptContractKeysByInsurance)
  const insuranceType = useOnboardingStore((s) => s.insuranceData.insuranceType)
  const ptReturning = useOnboardingStore((s) => s.ptReturning)
  // Self-paying patients sign Dok. 02b instead of Dok. 02 — recomputed when the answer changes
  const ptContracts = resolvePtContracts(ptContractKeysFor(ptContractKeysByInsurance, insuranceType, ptContractKeys), contracts)
  const steps: FlowStep[] = flowFor(
    cohort,
    { country: tenant?.country, contracts, paymentStep: resolvePaymentStep(tenant), testMode, skipSepaOverride },
    cohort === 'standard' ? null : { contracts: ptContracts, hasMandate: ptReturning?.hasMandate, insuranceType },
  )
  const TOTAL_STEPS = steps.length

  // Step position lives in the persisted store so reloads and the SEPA
  // redirect return the patient to where they were (not back to step 1).
  const currentStep = useOnboardingStore((s) => s.currentStep)
  const setCurrentStep = useOnboardingStore((s) => s.setCurrentStep)
  const [settingsLoading, setSettingsLoading] = useState(true)
  // 33% of patients span more than one day and 15% restart the flow: tell a
  // returning patient explicitly that their progress is intact.
  const [resumedAt] = useState<number>(() => useOnboardingStore.getState().currentStep)
  const [resumeDismissed, setResumeDismissed] = useState(false)
  const [handoffOpen, setHandoffOpen] = useState(false)
  const [maintenanceMode, setMaintenanceMode] = useState(false)
  const [bypassMaintenance, setBypassMaintenance] = useState(false)
  // Spent/expired link + a registration already on file. `?again=1` is the way
  // past it for a patient who genuinely needs to submit again.
  const [alreadyCompleted, setAlreadyCompleted] = useState(false)

  // Orgs that only admit magic-link patients: decide up front whether this
  // browser may use the form, instead of letting the patient fill every step and
  // be rejected by the API at the documents, SMS code or final submit
  // (2026-09-16: 14 patients stuck that way, P20025 waiting for an SMS that was
  // never requested). The API interceptor also raises the flag mid-flow, e.g.
  // when a session expires. Saved progress is untouched either way.
  const sessionToken = useOnboardingStore((s) => s.sessionToken)
  const [linkRejected, setLinkRejected] = useState(false)
  useEffect(() => {
    const onRejected = () => setLinkRejected(true)
    window.addEventListener(PATIENT_TOKEN_REQUIRED_EVENT, onRejected)
    return () => window.removeEventListener(PATIENT_TOKEN_REQUIRED_EVENT, onRejected)
  }, [])
  const hasUsableSession = isPatientSessionUsable(sessionToken)
  // Testers walk the form without a link; the server still guards real writes.
  const showLinkRequired = (linkRejected && !hasUsableSession) || (!!tenant?.requirePatientToken && !testMode && !hasUsableSession)

  // Clamp a restored step if the org's step count shrank in the meantime
  useEffect(() => {
    if (currentStep > TOTAL_STEPS) setCurrentStep(TOTAL_STEPS)
    if (currentStep < 1) setCurrentStep(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [TOTAL_STEPS])

  // Load settings from backend and verify URL params
  useEffect(() => {
    const initSettings = async () => {
      try {
        // Magic link (?ptk=): exchange the one-time token for a patient session
        const bootParams = new URLSearchParams(window.location.search)
        const ptk = bootParams.get('ptk')
        if (ptk) {
          // Remove the token from the address bar immediately — it must not
          // linger in history, screenshots, or shared URLs
          bootParams.delete('ptk')
          const cleanQuery = bootParams.toString()
          window.history.replaceState({}, '', window.location.pathname + (cleanQuery ? `?${cleanQuery}` : ''))

          try {
            const session = await patientLinkService.exchange(ptk)
            const store = useOnboardingStore.getState()
            if (store.patientNumber && store.patientNumber !== session.patientNumber) {
              // A different patient's data is in this browser — clear it
              store.reset()
            }
            useOnboardingStore.setState({
              // A hand-off link carries the other device's draft — restore it
              // before pinning the patient number so the step resumes too.
              ...(session.draft && typeof session.draft === 'object' ? session.draft : {}),
              patientNumber: session.patientNumber,
              patientNumberLocked: true,
            })
            useOnboardingStore.getState().setSessionToken(session.sessionToken)
            // Pin the cohort for the rest of the visit. A different cohort than the stored one
            // (e.g. a new link after an earlier visit) restarts at step 1 — the steps differ.
            const nextCohort = session.cohort ?? 'standard'
            const st = useOnboardingStore.getState()
            if (st.cohort !== nextCohort) useOnboardingStore.setState({ currentStep: 1, yousignRequestId: null, yousignDataHash: null, yousignDocuments: [], yousignDocumentMap: null })
            st.setCohort({
              cohort: nextCohort,
              test: !!session.test,
              contractKeys: session.variant?.contractKeys,
              contractKeysByInsurance: session.variant?.contractKeysByInsurance ?? null,
              returning: session.variant?.returning
                ? { privacySigned: session.variant.returning.privacySigned, billingSigned: session.variant.returning.billingSigned, hasMandate: session.variant.returning.hasMandate }
                : null,
            })
            // Returning patients: the earlier answers fill the store — only the new parts are asked
            const prefill = session.variant?.returning?.prefill
            if (prefill && typeof prefill === 'object') useOnboardingStore.setState(prefill as Partial<ReturnType<typeof useOnboardingStore.getState>>)
            // An admin test link runs the form in test mode: no real signature, nothing binding
            if (session.test) sessionStorage.setItem('test-mode', 'true')
            // A new link although the form is already done: say so first. "Fill in again"
            // continues with this link's session.
            if (session.alreadyCompleted && bootParams.get('again') !== '1') setAlreadyCompleted(true)
          } catch (err) {
            const code = getApiErrorCode(err)
            if (code === 'already_completed') {
              // Nothing went wrong — they are simply done. Say so on a page of
              // its own instead of a red toast over an empty form.
              if (bootParams.get('again') !== '1') setAlreadyCompleted(true)
            } else {
              const msgKey = code === 'link_expired' ? 'magicLink.expired'
                : code === 'link_used' ? 'magicLink.used'
                : 'magicLink.invalid'
              toast.error(t(msgKey), { duration: 10000 })
            }
          }
        }

        const settings = await settingsService.getPublicSettings()

        // Restore session state first
        if (sessionStorage.getItem('maintenance-bypass') === 'true') {
          setBypassMaintenance(true)
        }
        if (sessionStorage.getItem('test-mode') === 'true') {
          setTestMode(true)
          enableLogging()
          disableFlowEvents()
        }

        // Check URL params and verify against backend
        const urlParams = new URLSearchParams(window.location.search)

        const bypassParam = urlParams.get('bypass')
        if (bypassParam && settings.hasMaintenanceBypassPassword) {
          const valid = await settingsService.verifyPassword('maintenance_bypass', bypassParam)
          if (valid) {
            setBypassMaintenance(true)
            sessionStorage.setItem('maintenance-bypass', 'true')
          }
        }

        const testParam = urlParams.get('testmode')
        if (testParam && settings.hasTestModePassword) {
          const valid = await settingsService.verifyPassword('test_mode', testParam)
          if (valid) {
            const alreadyTest = sessionStorage.getItem('test-mode') === 'true'
            setTestMode(true)
            enableLogging()
            disableFlowEvents()
            sessionStorage.setItem('test-mode', 'true')
            // Fresh activation via the test link → start from a clean slate so
            // stale cached data from a previous (real or test) session doesn't
            // leak in. Reloads with the flag already set keep their progress.
            if (!alreadyTest) {
              sessionStorage.removeItem('submission-sent')
              useOnboardingStore.getState().reset()
            }
          }
        }

        setMaintenanceMode(settings.maintenanceMode)
      } catch {
        // Fail open: if backend is unreachable, don't block patients
        // Error intentionally not logged to console for patients
      } finally {
        setSettingsLoading(false)
      }
    }
    initSettings()
  }, [])

  // Step labels for flow events — an API for analytics (src/flow/flow.ts)
  const getStepLabel = (step: number): string => stepLabel(steps, step)

  // Track step progression — sends breadcrumb to backend once the patient number
  // is complete and valid. flowEventService dedupes + batches, so partial keystrokes
  // and back-navigation don't spam the funnel.
  const patientNumberFromStore = useOnboardingStore((s) => s.patientNumber)
  const digits = tenant?.patientNumberDigits ?? 5
  const patientNumberComplete = new RegExp(`^P\\d{${digits}}$`).test(patientNumberFromStore)
  useEffect(() => {
    if (settingsLoading || !patientNumberComplete || showLinkRequired || alreadyCompleted) return
    const stepLabel = getStepLabel(currentStep)
    const eventType = currentStep === TOTAL_STEPS ? 'flow_completed' : 'step_entered'
    logFlowEvent(patientNumberFromStore, stepLabel, eventType)
  }, [currentStep, patientNumberComplete, settingsLoading, alreadyCompleted]) // eslint-disable-line react-hooks/exhaustive-deps

  // Loading screen while fetching settings
  if (settingsLoading) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <Logo size={80} />
      </div>
    )
  }

  // Show maintenance page if enabled and not bypassed
  if (maintenanceMode && !bypassMaintenance) {
    return <MaintenancePage />
  }

  if (alreadyCompleted) {
    return <AlreadyCompletedPage onFillAgain={() => setAlreadyCompleted(false)} />
  }

  if (showLinkRequired) {
    return <LinkRequiredPage />
  }

  const handleNext = () => {
    if (currentStep < TOTAL_STEPS) {
      // A step is "completed" when the patient advances past it
      if (patientNumberComplete) {
        logFlowEvent(patientNumberFromStore, getStepLabel(currentStep), 'step_completed')
      }
      setCurrentStep(currentStep + 1)
    }
  }

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  // Test mode: wipe all persisted onboarding data and restart at step 1
  const handleTestReset = () => {
    sessionStorage.removeItem('submission-sent')
    useOnboardingStore.getState().reset()
  }

  const renderStep = () => {
    const step = steps[currentStep - 1]
    if (!step) return <Step1Welcome onNext={handleNext} />
    const previous = steps[currentStep - 2]
    switch (step.kind) {
      case 'welcome': return <Step1Welcome onNext={handleNext} />
      case 'personal': return <Step2PersonalData onNext={handleNext} onBack={handleBack} />
      case 'address': return <Step3AddressData onNext={handleNext} onBack={handleBack} />
      case 'insurance': return <Step4Insurance onNext={handleNext} onBack={handleBack} />
      case 'upload': return <Step4DocumentUpload onNext={handleNext} onBack={handleBack} />
      case 'summary': return <Step6DataSummary onNext={handleNext} onBack={handleBack} skipYousign={!hasContracts} />
      case 'contract':
        // The first contract has no "back": the signature request already exists
        return (
          <ContractSigningStep
            key={step.contract!.key}
            contract={step.contract!}
            onNext={handleNext}
            onBack={previous?.kind === 'contract' ? handleBack : undefined}
          />
        )
      case 'phone_otp': return <Step9PhoneVerification onNext={handleNext} onBack={handleBack} />
      case 'sepa': return <Step11SepaDebit onNext={handleNext} onBack={handleBack} />
      case 'card_payment': return <CardPaymentStep onNext={handleNext} onBack={handleBack} />
      case 'complete': return <Step12Complete />
      case 'pt_details': return <PtDetailsStep onNext={handleNext} onBack={currentStep > 1 ? handleBack : undefined} />
      case 'pt_payment': return <PtPaymentStep onNext={handleNext} onBack={handleBack} />
    }
  }

  return (
    <TestModeProvider value={testMode}>
    <div className="min-h-screen bg-muted flex flex-col">
      {testMode && (
        <div className="bg-amber-500 text-white text-center py-1.5 text-small font-medium">
          {t('testMode.banner')}
        </div>
      )}
      <ProgressBar
        currentStep={currentStep}
        totalSteps={TOTAL_STEPS}
        label={t(`progress.${getStepLabel(currentStep).replace(/^step\d+_?/, '')}`, { defaultValue: '' })}
        counter={t('progress.counter', { n: currentStep, total: TOTAL_STEPS })}
      />
      {patientNumberComplete && currentStep > 1 && currentStep < TOTAL_STEPS && !testMode && (
        <div className="hidden md:flex max-w-2xl mx-auto px-4 pt-3 justify-end">
          <button type="button" onClick={() => setHandoffOpen(true)} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary">
            <Smartphone className="w-3.5 h-3.5" />{t('handoff.trigger')}
          </button>
        </div>
      )}
      {handoffOpen && <HandoffModal onClose={() => setHandoffOpen(false)} />}
      {resumedAt > 1 && !resumeDismissed && currentStep < TOTAL_STEPS && (
        <div className="max-w-2xl mx-auto px-4 pt-4">
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 text-small text-foreground">
            <span>{t('app.welcomeBack', { step: currentStep, total: TOTAL_STEPS })}</span>
            <span className="flex items-center gap-3">
              <button type="button" className="underline text-primary" onClick={() => { useOnboardingStore.getState().reset(); setResumeDismissed(true) }}>
                {t('app.startOver')}
              </button>
              <button type="button" className="text-muted-foreground" aria-label="close" onClick={() => setResumeDismissed(true)}>×</button>
            </span>
          </div>
        </div>
      )}
      <LanguageSelector />
      <main className="pt-8 pb-12 px-4 flex-1">
        {renderStep()}
      </main>
      <LegalFooter />

      {/* Test mode: floating step navigator */}
      {testMode && (
        <div className="fixed bottom-4 right-4 bg-primary text-primary-foreground rounded-card shadow-lg p-3 z-50 text-small">
          <div className="font-medium mb-2 text-center">Test Mode</div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
              className="px-2 py-1 bg-white/20 rounded hover:bg-white/30"
              disabled={currentStep <= 1}
            >
              &larr;
            </button>
            <select
              value={currentStep}
              onChange={(e) => setCurrentStep(Number(e.target.value))}
              className="bg-white/20 rounded px-2 py-1 text-small text-primary-foreground"
            >
              {Array.from({ length: TOTAL_STEPS }, (_, i) => (
                <option key={i + 1} value={i + 1} className="text-foreground">
                  {i + 1} · {getStepLabel(i + 1).replace(/^step\d+_?/, '') || 'welcome'}
                </option>
              ))}
            </select>
            <button
              onClick={() => setCurrentStep(Math.min(TOTAL_STEPS, currentStep + 1))}
              className="px-2 py-1 bg-white/20 rounded hover:bg-white/30"
              disabled={currentStep >= TOTAL_STEPS}
            >
              &rarr;
            </button>
          </div>
          <button
            onClick={handleTestReset}
            className="mt-2 w-full px-2 py-1 bg-white/20 rounded hover:bg-white/30 text-small"
          >
            ↺ Reset &amp; Neustart
          </button>
        </div>
      )}
    </div>
    </TestModeProvider>
  )
}

export default App
